/**
 * shared-inbox.js
 * Domain use case for managing the Shared Prompts inbox lifecycle.
 */

const DEFAULT_TITLE = 'Untitled';
const DEFAULT_LABEL = 'Shared with me';

export function createSharedInbox({ fetchInbox, saveInboxItem, dismissInboxItem, normaliseInboxItem } = {}) {
  if (typeof fetchInbox !== 'function') {
    throw new Error('fetchInbox is required');
  }
  if (typeof saveInboxItem !== 'function') {
    throw new Error('saveInboxItem is required');
  }
  if (typeof dismissInboxItem !== 'function') {
    throw new Error('dismissInboxItem is required');
  }

  const normalise = typeof normaliseInboxItem === 'function' ? normaliseInboxItem : defaultNormaliseInboxItem;

  return {
    async fetch() {
      const payload = await fetchInbox();
      const items = extractItems(payload).map(normalise);
      return {
        items,
        source: 'service',
        response: payload ?? null,
      };
    },

    async save({ itemId, teamId } = {}) {
      const identifier = normaliseIdentifier(itemId);
      if (!identifier) {
        throw new Error('itemId is required');
      }
      const targetTeam = normaliseOptionalIdentifier(teamId);
      const response = await saveInboxItem(identifier, targetTeam);
      return {
        itemId: identifier,
        teamId: targetTeam,
        response: response ?? null,
      };
    },

    async dismiss({ itemId } = {}) {
      const identifier = normaliseIdentifier(itemId);
      if (!identifier) {
        throw new Error('itemId is required');
      }
      const response = await dismissInboxItem(identifier);
      return {
        itemId: identifier,
        response: response ?? null,
      };
    },
  };
}

function extractItems(payload) {
  if (Array.isArray(payload?.items)) {
    return payload.items;
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  return [];
}

function defaultNormaliseInboxItem(item) {
  const raw = item ?? null;
  const payload = isObject(raw?.payload) ? raw.payload : isObject(raw) ? raw : {};

  const idCandidate =
    (isObject(raw) ? raw.id ?? raw.item_id ?? raw.itemId : null) ??
    (isObject(payload) ? payload.id : null);
  const titleCandidate = isObject(payload) ? payload.title : null;
  const labelCandidate = isObject(raw) ? raw.label ?? raw.source_label : null;
  const sharedByCandidate = isObject(raw) ? raw.shared_by ?? raw.sharedBy : null;

  return {
    id: normaliseOptionalIdentifier(idCandidate),
    title: toStringSafe(titleCandidate, DEFAULT_TITLE),
    label: toStringSafe(labelCandidate, DEFAULT_LABEL),
    sharedBy: toStringSafe(sharedByCandidate, ''),
    raw,
  };
}

function normaliseIdentifier(value) {
  const trimmed = normaliseOptionalIdentifier(value);
  return trimmed || '';
}

function normaliseOptionalIdentifier(value) {
  if (value == null) {
    return null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
}

function toStringSafe(value, fallback = '') {
  if (typeof value === 'string') {
    return value;
  }
  if (value == null) {
    return fallback;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return fallback;
}

function isObject(value) {
  return value !== null && typeof value === 'object';
}

if (typeof window !== 'undefined') {
  window.AgileProDomain = window.AgileProDomain || {};
  window.AgileProDomain.share = window.AgileProDomain.share || {};
  window.AgileProDomain.share.createSharedInbox = createSharedInbox;
}
